import submit from "submit-form";

/**
 * Generic clipboard helpers for copy-paste functionality
 * Contains specific logic for different copy types (exercises, meals, etc.)
 */

/** Keep in sync with MAX_CUSTOM_WORKOUT_NAME_LENGTH in paste-exercises-program.js */
export const MAX_CUSTOM_WORKOUT_NAME_LENGTH = 200;

/**
 * Trim and cap length for session.workoutName suffix (same shape as program-workout-name saves).
 * @param {unknown} value
 * @returns {string} empty string if nothing usable
 */
export function clipCustomWorkoutName(value) {
  if (value == null) return "";
  const s = String(value).trim();
  if (s === "") return "";
  return s.length > MAX_CUSTOM_WORKOUT_NAME_LENGTH
    ? s.slice(0, MAX_CUSTOM_WORKOUT_NAME_LENGTH)
    : s;
}

/**
 * Function to save the exercise library with the exercise to paste of the original athlete
 * @param {*} library
 * @param {*} exercises
 * @returns
 */
const getExercisesOfExerciseLibrary = (library, exercises) => {
  const libraryToReturn = [];
  exercises.forEach((exercise) => {
    const { primary, secondary } = exercise;

    if (secondary) {
      const foundItem = library[primary]?.find(({ movement }) => {
        return movement === secondary;
      });

      if (foundItem) {
        libraryToReturn.push(foundItem);
      }
    }
  });
  return libraryToReturn;
};

/**
 * Strip the optional per-athlete `athleteNotes` field from exercises so it
 * does not carry over on copy/duplicate.
 * @param {Array<Object>} exercises
 * @returns {Array<Object>}
 */
export const stripAthleteNotes = (exercises) =>
  exercises.map(({ athleteNotes, ...rest }) => rest);

/**
 * Copy exercises to clipboard
 * Handles the logic for copying exercises including exercise library data
 * @param {Array|Object} exercises - Exercises to copy (can be array or single object)
 * @param {Object} athlete - Athlete object with exerciseLibrary
 * @param {string|undefined} workoutNameSuffix - Optional session.workoutName suffix for program paste (omit for single-exercise / calendar copy)
 * @param {Array|undefined} sections - Optional section separators (text dividers) to carry through paste
 */
export const copyExercises = (
  exercises,
  athlete,
  workoutNameSuffix,
  sections
) => {
  if (exercises && athlete) {
    const exercisesArray = Array.isArray(exercises) ? exercises : [exercises];
    if (exercisesArray.length > 0) {
      const cleanedExercises = stripAthleteNotes(exercisesArray);
      const { exerciseLibrary } = athlete;
      const exercisesOfExerciseLibrary = getExercisesOfExerciseLibrary(
        exerciseLibrary,
        cleanedExercises
      );
      const body = {
        exercises: cleanedExercises,
        originalAthleteLibrary: exercisesOfExerciseLibrary,
      };
      if (workoutNameSuffix !== undefined) {
        body.workoutNameToCopy = clipCustomWorkoutName(workoutNameSuffix);
      }
      if (Array.isArray(sections) && sections.length > 0) {
        // Only the identity fields + order are needed to re-place the
        // section on paste. A workout note carries `type`/`description`
        // instead of `name` (see js/utils/section-types.js) — preserved here
        // so it survives the clipboard round trip instead of being dropped.
        body.sections = sections.map((s) =>
          s.type === "workout-note"
            ? {
                type: "workout-note",
                description: s.description ?? "",
                order: s.order ?? 0,
              }
            : { type: "normal", name: s.name ?? "", order: s.order ?? 0 }
        );
      }
      submit(`/actions/exercise/copy-exercises`, {
        method: "POST",
        body,
      });
    }
  }
};

/**
 * Copy meal to clipboard
 * Handles the logic for copying meals
 * Supports copying between different plan modes (detailed/simplified) and templates
 *
 * @param {Object} meal - Meal object to copy (must have foods array)
 * @param {Object} day - Day object containing the meal (has date, dayType, optionId)
 * @param {Object} query - Query parameters (mealPlan, athlete, week, etc.)
 * @param {string} planMode - Plan mode: "detailed" or "simplified"
 */
export const copyMeal = (meal, day, query, planMode) => {
  if (!meal || !day || !query) {
    return;
  }

  const { foods } = meal;
  const { athlete } = query;

  // Validate meal has foods
  if (!Array.isArray(foods) || foods.length === 0) {
    return;
  }

  // Determine if this is a template (no athlete = template)
  const isTemplate = !athlete;

  // Build source metadata (minimal - only what's needed for paste operation)
  // Note: Currently only isTemplate is used, but keeping sourcePlanMode for potential future use
  const sourceMetadata = {
    sourcePlanMode: planMode || "detailed",
    isTemplate: isTemplate,
  };

  // Call endpoint (endpoint will save to localStorage, show flash, and refresh)
  submit(`/actions/meal-planner/copy-meal`, {
    method: "POST",
    body: {
      foods: foods,
      metadata: sourceMetadata,
    },
  });
};

/**
 * Copy food to clipboard
 * Handles the logic for copying a single food item
 *
 * @param {Object} food - Food object to copy
 * @param {Object} day - Day object containing the food (has date, dayType, optionId)
 * @param {Object} query - Query parameters (mealPlan, athlete, week, etc.)
 * @param {string} planMode - Plan mode: "detailed" or "simplified"
 */
export const copyFood = (food, day, query, planMode) => {
  if (!food || !day || !query) {
    return;
  }

  const { athlete } = query;

  // Determine if this is a template (no athlete = template)
  const isTemplate = !athlete;

  // Build source metadata
  const sourceMetadata = {
    sourcePlanMode: planMode || "detailed",
    isTemplate: isTemplate,
  };

  // Wrap food in foods array (same format as copyMeal)
  const foods = [food];

  // Call endpoint (endpoint will save to localStorage, show flash, and refresh)
  submit(`/actions/meal-planner/copy-meal`, {
    method: "POST",
    body: {
      foods: foods,
      metadata: sourceMetadata,
    },
  });
};
